var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/** @jsx jsx */
import { jsx, React, ReactRedux, css } from 'jimu-core';
import { NavLink, NavItem, Nav, NavMenu } from './nav';
import { ScrollList } from './scrollable';
import { Tooltip } from './tooltip';
const { useMemo } = React;
const { useSelector } = ReactRedux;
const useStyle = (vertical, showIcon, showText, keepPaddingWhenOnlyIcon = false) => {
    const onlyIcon = showIcon && !showText;
    return useMemo(() => {
        const width = vertical ? '100%' : undefined;
        return css `
      *:focus{
        box-shadow: none !important;
      }
      width: 100%;
      height: 100%;
      flex-wrap: nowrap;
      width: ${width};
      flex-wrap: nowrap;
      &.nav-tabs .nav-link.active {
        background-color: unset;
      }
      .nav-item {
        overflow: unset;
        flex-shrink: 0;
        .jimu-nav-link-wrapper{
          text-overflow: unset;
          overflow: unset;
        }
      }
      /* Remove padding and margin when only show icon in nav-item */
      ${onlyIcon && css `
        .nav-item {
          line-height: 1 !important;
          .nav-link {
            ${!keepPaddingWhenOnlyIcon && css `padding: 0 !important;`}
            line-height: 1 !important;
          }
          .jimu-nav-link-wrapper {
            .jimu-icon {
              margin-left: unset;
              margin-right: unset;
            }
          }
        }
      `}
      .scroll-list ul.list {
        padding: 0;
        margin: 0;
      }
    `;
    }, [vertical, onlyIcon, keepPaddingWhenOnlyIcon]);
};
export const useNavMenuStyle = (gap) => {
    return useMemo(() => {
        return css `
      *:focus{
        box-shadow: none !important;
      }
      flex-wrap: nowrap;

      .dropdown-menu-inline {
        padding: 0 !important;
        .nav-item {
          margin-top:${gap} !important;
        }
      }
  `;
    }, [gap]);
};
const getNavLinkAriaProps = (role, active) => {
    const ariaProps = {};
    switch (role) {
        case 'menuitem':
            if (active) {
                ariaProps['aria-current'] = 'page';
            }
            break;
        case 'tab':
            ariaProps['aria-selected'] = active;
            break;
        case 'button':
            ariaProps['aria-pressed'] = active;
            break;
    }
    return ariaProps;
};
const useNavLinkRole = (listRole) => {
    switch (listRole) {
        case 'menubar':
        case 'menu':
            return 'menuitem';
        case 'tablist':
            return 'tab';
        default:
            return 'button';
    }
};
const useNavTag = (listRole, scrollable) => {
    switch (listRole) {
        case 'menubar':
        case 'menu':
            return scrollable ? 'nav' : 'ul';
        case 'tablist':
            return scrollable ? 'div' : 'ul';
        default:
            return 'div';
    }
};
export const Navigation = (props) => {
    const { vertical = false, type = 'default', data, isActive, scrollable = false, showIcon, activedIcon: propActivedIcon, alternateIcon, showText = true, showTitle = false, iconPosition, gap, isUseNativeTitle = false, role = 'menu', keepPaddingWhenOnlyIcon, onLinkClick } = props, others = __rest(props, ["vertical", "type", "data", "isActive", "scrollable", "showIcon", "activedIcon", "alternateIcon", "showText", "showTitle", "iconPosition", "gap", "isUseNativeTitle", "role", "keepPaddingWhenOnlyIcon", "onLinkClick"]);
    const queryObject = useSelector((state) => state.queryObject);
    const navMenuStyle = useNavMenuStyle(gap);
    const navLinkRole = useNavLinkRole(role);
    // if vertical menu has menu item with foldable submenu, add 20 padding to left or right side (depend on text align value) to avoid overlap of menu name content and dropdown button
    const needAddPadding = useMemo(() => {
        const hasSubmenu = data.some((item) => { var _a; return ((_a = item === null || item === void 0 ? void 0 : item.subs) === null || _a === void 0 ? void 0 : _a.length) > 0; });
        return vertical && hasSubmenu && others.submenuMode === 'foldable';
    }, [data, vertical, others.submenuMode]);
    const items = React.useMemo(() => {
        var _a;
        return (_a = data === null || data === void 0 ? void 0 : data.map((item, idx) => {
            var _a, _b, _c, _d;
            const active = isActive === null || isActive === void 0 ? void 0 : isActive(item);
            const activedIcon = propActivedIcon || item.icon || alternateIcon;
            const icon = active ? activedIcon : item.icon || alternateIcon;
            const text = showText ? item.name : '';
            const isShowNativeTitle = showTitle && isUseNativeTitle;
            const isShowTooltipTitle = showTitle && !isUseNativeTitle;
            const navLink = (jsx(NavLink, Object.assign({ draggable: false, iconPosition: iconPosition, target: item.target, iconSize: (_a = icon === null || icon === void 0 ? void 0 : icon.properties) === null || _a === void 0 ? void 0 : _a.size, to: item, icon: showIcon ? icon === null || icon === void 0 ? void 0 : icon.svg : '', active: active, caret: !!((_b = item === null || item === void 0 ? void 0 : item.subs) === null || _b === void 0 ? void 0 : _b.length), queryObject: queryObject, role: navLinkRole }, getNavLinkAriaProps(navLinkRole, active), { "aria-label": item.name, className: needAddPadding ? 'padding-plus-20' : '', "aria-controls": item.navLinkAriaControls, onClick: onLinkClick ? () => { onLinkClick(item); } : undefined }), text));
            const dropMenu = ((_c = item === null || item === void 0 ? void 0 : item.subs) === null || _c === void 0 ? void 0 : _c.length) > 0
                ? (jsx(NavMenu, { css: navMenuStyle }, (_d = item.subs) === null || _d === void 0 ? void 0 : _d.map((item, index) => {
                    var _a;
                    const subNavLinkText = showText ? item.name : '';
                    const submenuActive = isActive === null || isActive === void 0 ? void 0 : isActive(item);
                    let subsNavItem = jsx(NavItem, { key: index, title: isShowNativeTitle ? subNavLinkText : undefined, role: 'none' },
                        jsx(NavLink, Object.assign({ draggable: false, active: submenuActive, to: item, iconPosition: iconPosition, target: item.target, queryObject: queryObject, icon: showIcon ? (_a = item === null || item === void 0 ? void 0 : item.icon) === null || _a === void 0 ? void 0 : _a.svg : '', role: navLinkRole }, getNavLinkAriaProps(navLinkRole, submenuActive), { "aria-label": item.name, "aria-controls": item.navLinkAriaControls, onClick: onLinkClick ? () => { onLinkClick(item); } : undefined }), subNavLinkText));
                    if (isShowTooltipTitle) {
                        subsNavItem = jsx(Tooltip, { title: subNavLinkText, key: index }, subsNavItem);
                    }
                    return subsNavItem;
                })))
                : undefined;
            let navItem = jsx(NavItem, { key: idx, title: isShowNativeTitle ? item.name : undefined, role: 'none' },
                navLink,
                dropMenu);
            if (isShowTooltipTitle) {
                navItem = jsx(Tooltip, { title: item.name, key: idx }, navItem);
            }
            return navItem;
        })) !== null && _a !== void 0 ? _a : [];
    }, [data, isActive, propActivedIcon, alternateIcon, showText, showTitle, isUseNativeTitle, iconPosition, showIcon, queryObject, needAddPadding, onLinkClick, navMenuStyle, navLinkRole]);
    const wrapper = scrollable ? jsx(ScrollList, { autoArrow: true, duration: 300, vertical: vertical, items: items, role: role, listTag: "ul" }) : items;
    const style = useStyle(vertical, showIcon, showText, keepPaddingWhenOnlyIcon);
    const tag = useNavTag(role, scrollable);
    return (jsx(Nav, Object.assign({}, others, { gap: gap, css: style, navbar: true, iconOnly: !showText, underline: type === 'underline', pills: type === 'pills', tabs: type === 'tabs', vertical: vertical, justified: !vertical, tag: tag, role: scrollable ? undefined : role }), wrapper));
};
